import React from "react";
import styled from "styled-components";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { SxProps, Theme } from "@mui/material/styles";

import Button from "@ingka/button";
import TextArea from "@ingka/text-area";
import FormField from "@ingka/form-field";
import { colourStaticIkeaBrandYellow, colourTextAndIcon3 } from "@ingka/variables/colours-css";
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from "@ingka/modal";
import Checkbox from "@ingka/checkbox";
import Toast from "@ingka/toast";
import InlineMessage from "@ingka/inline-message";
import informationCircle from "@ingka/ssr-icon/paths/information-circle";
import { space150, space200 } from "@ingka/variables/design-tokens";

import { SrOnly } from "../styles";

const StyledModalHeader = styled(ModalHeader)`
	background-color: ${colourStaticIkeaBrandYellow};
`;

const StyledTextArea = styled(TextArea)`
	margin-top: ${space200};

	label {
		display: flex;
	}
`;

const StyledInlineMessage = styled(InlineMessage)`
	margin-bottom: ${space150};
`;

const placeholderSx: SxProps<Theme> = {
	"& .MuiInputBase-input::placeholder": {
		color: colourTextAndIcon3,
		opacity: 1,
	},
};

interface ScheduleChangesModalProps {
	showScheduleChangesModal: boolean;
	setShowScheduleChangesModal: (val: boolean) => void;
	schedulingComment: string;
	setSchedulingComment: (val: string) => void;
	sendCopyEmail: boolean;
	setSendCopyEmail: (val: boolean) => void;
	sendScheduleChangesLoading: boolean;
	setSendScheduleChangesLoading: (val: boolean) => void;
	sendScheduleChanges: () => void;
	scheduleDate: Dayjs | null;
	setScheduleDate: (val: Dayjs | null) => void;
	scheduleToastMessage: string;
	setScheduleToastMessage: (val: string) => void;
}

const ScheduleChangesModal: React.FC<ScheduleChangesModalProps> = ({
	showScheduleChangesModal,
	setShowScheduleChangesModal,
	schedulingComment,
	setSchedulingComment,
	sendCopyEmail,
	setSendCopyEmail,
	sendScheduleChangesLoading,
	setSendScheduleChangesLoading,
	sendScheduleChanges,
	scheduleDate,
	setScheduleDate,
	scheduleToastMessage,
	setScheduleToastMessage,
}) => {
	const minDate = new Date(new Date().setHours(0, 0, 0, 0)).setDate(new Date().getDate() + 1);

	return (
		<>
			<SrOnly id="loading-message" role="status" aria-live="polite" aria-atomic="true">
				{sendScheduleChangesLoading && "Loading"}
			</SrOnly>
			<Modal
				visible={showScheduleChangesModal}
				escapable={false}
				handleCloseBtn={() => setShowScheduleChangesModal(false)}
			>
				<Sheets
					id="schedule-change-modal"
					aria-labelledby="scheduleChangeModalTitleId"
					aria-describedby="importantInfoId"
					header={
						<StyledModalHeader
							title="Choose your date and time to schedule changes"
							titleId="scheduleChangeModalTitleId"
						/>
					}
					footer={
						<ModalFooter>
							<Button
								size="small"
								text="Cancel"
								type="secondary"
								onClick={() => {
									setScheduleDate(null);
									setShowScheduleChangesModal(false);
									setSchedulingComment("");
								}}
							/>
							<Button
								id="schedule-changes-button"
								size="small"
								text="Send"
								disabled={scheduleDate === null}
								loading={sendScheduleChangesLoading}
								loadingSrText={"Loading"}
								type="emphasised"
								onClick={sendScheduleChanges}
							/>
						</ModalFooter>
					}
				>
					<ModalBody>
						<div id="date-picker">
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DateTimePicker
									data-testid="schedule-time"
									label="Schedule time"
									value={scheduleDate}
									minutesStep={15}
									skipDisabled={true}
									onChange={(newValue) => setScheduleDate(newValue)}
									minDateTime={dayjs(minDate)}
									slotProps={{
										textField: {
											sx: placeholderSx,
											helperText: "Time is selected in local timezone",
										},
									}}
								/>
							</LocalizationProvider>
						</div>
						<FormField>
							<StyledTextArea
								id="open-feedback"
								label="Comments"
								rows={8}
								value={schedulingComment}
								onChange={(e) => {
									setSchedulingComment(e.target.value);
								}}
							/>
						</FormField>
						<StyledInlineMessage
							id="importantInfoId"
							body={
								<>
									Scheduled changes <strong>can't</strong> be removed/modified before <strong>30min</strong> of
									scheduled time
								</>
							}
							ssrIcon={informationCircle}
							variant="informative"
							subtle
						/>
						<FormField>
							<Checkbox
								id="send-copy-email-scheduling"
								label="Send me a copy of the email"
								value={sendCopyEmail.toString()}
								checked={sendCopyEmail}
								onChange={(ev) => setSendCopyEmail(ev.target.checked)}
							/>
						</FormField>
					</ModalBody>
				</Sheets>
			</Modal>
			<SrOnly role="status" aria-live="polite" aria-atomic="true">
				{scheduleToastMessage}
			</SrOnly>
			<Toast
				data-testid="message"
				text={scheduleToastMessage}
				isOpen={!!scheduleToastMessage}
				onCloseRequest={() => setScheduleToastMessage("")}
				ariaLabelCloseBtn="Dismiss notification"
			/>
		</>
	);
};

export default ScheduleChangesModal;
